import { useState } from "react";
import { useNavigate } from "react-router";
import { BatchCheckInModal } from "@/features/batches/components/BatchCheckInModal";

interface Props {
  /** Batch id of the document currently loaded in VerificationStation. */
  currentDocBatchId: string | null;
}

/**
 * Self check-in button for the Verification Station toolbar. Visible whenever
 * the VS has a document loaded — R-151 dropped the prior batch-session
 * affinity gate (`useCurrentBatch().data.batch.id === currentDocBatchId`)
 * because it left operators stranded when they opened a doc directly from
 * the batch list (no warmed batch_session) instead of via "Get Next Document".
 * The checkin endpoint is safe to call even when no batch_session exists —
 * worst case it's a no-op release. On success, navigates to `/batches` —
 * R-161 redirected this away from `/queues` because the queue UX isn't
 * operator-ready until R-162 ships; the batches list is the safe landing
 * page that lets the operator pick up another batch or verify the release.
 */
export function VerificationCheckInButton({ currentDocBatchId }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (currentDocBatchId === null) return null;

  return (
    <>
      <button
        type="button"
        className="vs-detach-btn"
        onClick={() => setOpen(true)}
        title="Release this batch back to the queue"
        data-testid="vs-checkin-btn"
      >
        Check In Batch
      </button>
      <BatchCheckInModal
        batchId={currentDocBatchId}
        open={open}
        onClose={() => setOpen(false)}
        onSuccess={() => {
          setOpen(false);
          // R-161 — land on /batches (not /queues) until R-162 ships the
          // continuous-work queue UX.
          navigate("/batches");
        }}
      />
    </>
  );
}
